import React, { useState } from "react";

import {
  Box,
  Button,
  Container,
  Typography,
  Tabs,
  Tab,
  Badge,
  Chip,
} from "@mui/material";
import { useGo, useList, useOne } from "@refinedev/core";
import { useParams } from "react-router-dom";
import PlaygroundBreadcrumbs from "../../components/common/playground-breadcrumbs";
import { Batch, Brand, CopywriterJobToGoCounts } from "../playground/types";
import { BatchCreate } from "./create";
import { BatchEdit } from "./edit";
import { useAuthStore } from "../../authProvider";
import { downloadHelper } from "../../utils/download";
import { exportTimeTracking } from "../../api/batches";

export const BatchList: React.FC = () => {
  const go = useGo();

  const { user } = useAuthStore();

  const { brandId } = useParams();

  const [batchModalOpen, setBatchModalOpen] = useState<
    "closed" | "create" | string
  >("closed");

  const [showArchived, setShowArchived] = useState(false);

  // Determine if we should filter for client-owned batches
  // CLIENT and COPYWRITER_CLIENT see only client-owned batches
  // ADMIN and MANAGER see all batches (no filter)
  // COPYWRITER sees only non-client-owned batches
  const getClientOwnedFilter = () => {
    if (user?.isClientRelated) return true; // Clients see only client-owned
    if (user?.isAdmin || user?.isManager) return undefined; // Admins/Managers see all
    return false; // Copywriters see non-client-owned
  };

  const { data, isError, error, isLoading } = useList({
    resource: "batch",
    pagination: {
      current: 1,
    },
    meta: {
      params: {
        brand_id: brandId,
        archived: showArchived,
        client_owned_only: getClientOwnedFilter(),
      },
    },
  });

  const { data: brandData, isLoading: brandLoading } = useOne({
    resource: "brand",
    id: brandId,
  });

  const batches: Batch[] = data?.data as Batch[];
  const brand: Brand = brandData?.data as Brand;

  const extra: {
    active_total: number;
    archived_total: number;
    copywriter_jobs_to_go: CopywriterJobToGoCounts;
  } = data?.extra as {
    active_total: number;
    archived_total: number;
    copywriter_jobs_to_go: CopywriterJobToGoCounts;
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        flex: 1,
        display: "flex",
        p: 4,
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <PlaygroundBreadcrumbs brandName={brand?.name} brandId={brand?.id} />

        {isLoading && <div>Loading...</div>}

        {isError && <div>Error: {error.message}</div>}

        {batchModalOpen === "create" && !brandLoading && (
          <BatchCreate
            brandData={brand}
            onClose={() => {
              setBatchModalOpen("closed");
            }}
          />
        )}

        {batchModalOpen !== "closed" && batchModalOpen !== "create" && (
          <BatchEdit
            batchId={batchModalOpen}
            batchData={
              batches?.find((batch) => batch.id === batchModalOpen) as Batch
            }
            onClose={() => {
              setBatchModalOpen("closed");
            }}
          />
        )}

        {batches && (
          <>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="h4" component="h1" sx={{ mb: 0 }}>
                  Batches
                </Typography>
                <Tabs
                  value={showArchived ? 1 : 0}
                  onChange={(_, newValue) => setShowArchived(Boolean(newValue))}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  <Tab
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <span>Active</span>
                        {(extra?.active_total || 0) > 0 && (
                          <Badge
                            badgeContent={extra?.active_total}
                            color="warning"
                          />
                        )}
                      </Box>
                    }
                  />
                  <Tab
                    iconPosition="start"
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <span>Archived</span>
                        {(extra?.archived_total || 0) > 0 && (
                          <Badge
                            badgeContent={extra?.archived_total}
                            color="error"
                          />
                        )}
                      </Box>
                    }
                  />
                </Tabs>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                {user?.isAdmin && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={async () => {
                      await downloadHelper(() =>
                        exportTimeTracking({
                          brandId: brandId,
                        })
                      );
                    }}
                  >
                    Export time tracking
                  </Button>
                )}
                {(user?.isAdmin ||
                  user?.isManager ||
                  user?.isClientRelated) && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setBatchModalOpen("create")}
                  >
                    Create New Batch
                  </Button>
                )}
              </Box>

              {batches.length > 1 &&
                user?.isCopywriter &&
                extra?.copywriter_jobs_to_go &&
                (extra.copywriter_jobs_to_go.to_go_as_editor > 0 ||
                  extra.copywriter_jobs_to_go.to_go_as_reviewer > 0) && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">In totale</Typography>
                    {extra.copywriter_jobs_to_go.to_go_as_editor > 0 && (
                      <Chip
                        label={
                          extra.copywriter_jobs_to_go.to_go_as_editor +
                          " da editare"
                        }
                        color="info"
                        sx={{
                          borderRadius: 2,
                          height: 32,
                          cursor: "default",
                        }}
                      />
                    )}
                    {extra.copywriter_jobs_to_go.to_go_as_reviewer > 0 && (
                      <Chip
                        label={
                          extra.copywriter_jobs_to_go.to_go_as_reviewer +
                          " da revisionare"
                        }
                        color="secondary"
                        sx={{
                          borderRadius: 2,
                          height: 32,
                          cursor: "default",
                        }}
                      />
                    )}
                  </Box>
                )}
            </Box>
            <>
              {batches?.map((batch: Batch) => {
                return (
                  <Box
                    key={batch.id}
                    sx={{
                      width: "100%",
                      p: 2,
                      border: "1px solid #ccc",
                      borderRadius: 2,
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      go({
                        to: {
                          resource: "job",
                          action: "list",
                          meta: {
                            batchId: batch.id,
                          },
                        },
                      });
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="h6">
                        {batch.name}
                        {batch.client_owned &&
                          (batch.created_by_name || batch.created_by_surname) && (
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{ ml: 1, color: "text.secondary" }}
                            >
                              - Created by {batch.created_by_name}{" "}
                              {batch.created_by_surname}
                              {batch.created_at &&
                                ` on ${new Date(batch.created_at).toLocaleDateString()}`}
                            </Typography>
                          )}
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {batch.copywriter_jobs_to_go &&
                          (batch.copywriter_jobs_to_go.to_go_as_editor > 0 ||
                            batch.copywriter_jobs_to_go.to_go_as_reviewer >
                              0) && (
                            <>
                              <Typography variant="body2">
                                &bull; copy
                              </Typography>
                              {batch.copywriter_jobs_to_go.to_go_as_editor >
                                0 && (
                                <Chip
                                  label={
                                    batch.copywriter_jobs_to_go
                                      .to_go_as_editor + " da editare"
                                  }
                                  size="small"
                                  color="info"
                                  sx={{
                                    borderRadius: 2,
                                    height: 32,
                                  }}
                                />
                              )}
                              {batch.copywriter_jobs_to_go.to_go_as_reviewer >
                                0 && (
                                <Chip
                                  label={
                                    batch.copywriter_jobs_to_go
                                      .to_go_as_reviewer + " da revisionare"
                                  }
                                  size="small"
                                  color="secondary"
                                  sx={{
                                    borderRadius: 2,
                                    height: 32,
                                  }}
                                />
                              )}
                            </>
                          )}

                        {!user?.isClientRelated && batch?.advanced_stats && (
                          <>
                            <Typography variant="body2">
                              &bull; stats
                            </Typography>
                            <Chip
                              label={
                                batch.advanced_stats.average_words_per_job +
                                " words/sku"
                              }
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{
                                borderRadius: 2,
                                height: 32,
                              }}
                            />
                            <Chip
                              label={
                                batch.advanced_stats.translation_metrics
                                  .total_words + " total words"
                              }
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{
                                borderRadius: 2,
                                height: 32,
                              }}
                            />
                            <Chip
                              label={
                                batch.advanced_stats.translation_metrics
                                  .word_reuse_percentage + "% reuse"
                              }
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{
                                borderRadius: 2,
                                height: 32,
                              }}
                            />
                          </>
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {batch.pending_configuration && (
                        <Chip
                          label="Pending Configuration"
                          size="small"
                          color="warning"
                          sx={{
                            borderRadius: 2,
                            height: 32,
                          }}
                        />
                      )}

                      {(user?.isAdmin || user?.isManager) &&
                        batch.client_owned && (
                          <Chip
                            label="Client owned"
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{
                              borderRadius: 2,
                              height: 32,
                            }}
                          />
                        )}

                      {(user?.isAdmin ||
                        user?.isManager ||
                        user?.isClientRelated) && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();

                            if (batch.id) {
                              setBatchModalOpen(batch.id);
                            }
                          }}
                        >
                          Edit
                        </Button>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </>
          </>
        )}
      </Box>
    </Container>
  );
};
